import { useCallback, useState, type FormEvent } from "react";
import type { ApiError } from "../api/client";
import { useApi } from "../api/context";
import type { DemoLink, LinkResult } from "../api/types";
import { CopyButton } from "../components/CopyButton";
import { Field } from "../components/Field";
import { StatusBadge } from "../components/StatusBadge";
import { fieldMessage, toApiError } from "../lib/errors";
import { formatDate } from "../lib/format";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { useLoader } from "../lib/useLoader";
import { useWorkspace } from "./workspace";

const EXPIRY_OPTIONS = [3, 7, 14, 30];

interface LinkForm {
  email: string;
  name: string;
  note: string;
  days: number;
  sendEmail: boolean;
}

const EMPTY_FORM: LinkForm = { email: "", name: "", note: "", days: 7, sendEmail: true };

export function LinksPage() {
  useDocumentTitle("Demo links · TryLook");
  const api = useApi();
  const { tenantId, tenantName } = useWorkspace();
  const loadLinks = useCallback(() => api.listLinks(tenantId), [api, tenantId]);
  const links = useLoader(loadLinks);

  const [form, setForm] = useState<LinkForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [issued, setIssued] = useState<{ result: LinkResult; emailRequested: boolean } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyLinkId, setBusyLinkId] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);
    setIssued(null);
    setNotice(null);
    try {
      const result = await api.createLink(
        {
          recipient_email: form.email.trim(),
          recipient_name: form.name.trim() || undefined,
          note: form.note.trim() || undefined,
          expires_in_days: form.days,
          send_email: form.sendEmail,
        },
        tenantId,
      );
      setIssued({ result, emailRequested: form.sendEmail });
      setForm({ ...EMPTY_FORM, days: form.days, sendEmail: form.sendEmail });
      links.reload();
    } catch (error) {
      setFormError(toApiError(error));
    } finally {
      setSubmitting(false);
    }
  }

  async function resend(link: DemoLink) {
    if (!window.confirm(`Email a fresh link to ${link.recipient_email}? The link they have now will stop working.`)) {
      return;
    }
    setBusyLinkId(link.link_id);
    setNotice(null);
    try {
      const result = await api.resendLink(link.link_id, 7, tenantId);
      setIssued({ result, emailRequested: true });
      links.reload();
    } catch (error) {
      setNotice(toApiError(error).message);
    } finally {
      setBusyLinkId(null);
    }
  }

  async function revoke(link: DemoLink) {
    if (!window.confirm(`Revoke the demo link for ${link.recipient_email}? It will stop working immediately.`)) {
      return;
    }
    setBusyLinkId(link.link_id);
    setNotice(null);
    try {
      await api.revokeLink(link.link_id, tenantId);
      setNotice(`The link for ${link.recipient_email} has been revoked.`);
      links.reload();
    } catch (error) {
      setNotice(toApiError(error).message);
    } finally {
      setBusyLinkId(null);
    }
  }

  const formProblem = formError && formError.details.length === 0 ? formError.message : null;

  return (
    <>
      <div className="page-head">
        <h1>Demo links</h1>
        <p>
          Send a lead their own expiring link. When they open it, the AI guide runs a live demo of {tenantName} and
          can book a call with your team.
        </p>
      </div>

      <div className="grid-2">
        <form className="card stack" onSubmit={(event) => void submit(event)} noValidate data-tour="new-link-form">
          <h2 className="card-title">New link</h2>
          {formProblem && (
            <p className="alert alert-error" role="alert">
              {formProblem}
            </p>
          )}
          <Field label="Recipient email" error={fieldMessage(formError, "recipient_email")}>
            {(props) => (
              <input
                {...props}
                type="email"
                autoComplete="off"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
                required
              />
            )}
          </Field>
          <Field label="Recipient name (optional)" error={fieldMessage(formError, "recipient_name")}>
            {(props) => (
              <input
                {...props}
                autoComplete="off"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            )}
          </Field>
          <Field
            label="Personal note (optional)"
            hint="Shown in the invite email."
            error={fieldMessage(formError, "note")}
          >
            {(props) => (
              <textarea
                {...props}
                maxLength={500}
                value={form.note}
                onChange={(event) => setForm({ ...form, note: event.target.value })}
              />
            )}
          </Field>
          <Field label="Link expires after">
            {(props) => (
              <select
                {...props}
                value={form.days}
                onChange={(event) => setForm({ ...form, days: Number(event.target.value) })}
              >
                {EXPIRY_OPTIONS.map((days) => (
                  <option key={days} value={days}>
                    {days} days
                  </option>
                ))}
              </select>
            )}
          </Field>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={form.sendEmail}
              onChange={(event) => setForm({ ...form, sendEmail: event.target.checked })}
            />
            Email the link to the recipient now
          </label>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Creating…" : "Create link"}
          </button>
        </form>

        {issued && (
          <section className="card stack" aria-live="polite">
            <h2 className="card-title">Link ready</h2>
            {issued.result.email_sent ? (
              <p className="alert alert-success">
                We emailed it to <strong>{issued.result.link.recipient_email}</strong>.
              </p>
            ) : issued.emailRequested ? (
              <p className="alert alert-error">
                We couldn't email it just now. Copy the link below and send it yourself.
              </p>
            ) : (
              <p>Copy the link below and share it with your lead.</p>
            )}
            <div className="copy-field">
              <code>{issued.result.link.url}</code>
              <CopyButton value={issued.result.link.url ?? ""} label="Copy link" />
            </div>
            <p className="field-hint">
              Expires {formatDate(issued.result.link.expires_at)}. For security, the full link is only shown once.
            </p>
          </section>
        )}
      </div>

      <section id="sent-links" className="card" data-tour="sent-links">
        <div className="card-head">
          <h2 className="card-title">Sent links</h2>
          {links.loading && <span className="spinner" role="status" aria-label="Loading links" />}
        </div>
        {notice && (
          <p className="alert alert-info" role="status">
            {notice}
          </p>
        )}
        {links.error && (
          <p className="alert alert-error" role="alert">
            {links.error.message}
          </p>
        )}
        {links.data && links.data.links.length === 0 && (
          <p className="empty">No links yet. Create one to invite your first lead.</p>
        )}
        {links.data && links.data.links.length > 0 && (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">Recipient</th>
                  <th scope="col">Status</th>
                  <th scope="col">Created</th>
                  <th scope="col">Expires</th>
                  <th scope="col">Opens</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {links.data.links.map((link) => {
                  const expired = Date.parse(link.expires_at) <= links.loadedAt;
                  return (
                    <tr key={link.link_id}>
                      <td>
                        <div className="cell-primary">{link.recipient_name ?? link.recipient_email}</div>
                        {link.recipient_name && <div className="muted">{link.recipient_email}</div>}
                      </td>
                      <td>
                        <StatusBadge status={link.status === "active" && expired ? "expired" : link.status} />
                      </td>
                      <td>{formatDate(link.created_at)}</td>
                      <td>{formatDate(link.expires_at)}</td>
                      <td>{link.open_count}</td>
                      <td>
                        {link.status === "active" && (
                          <div className="actions">
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              disabled={busyLinkId === link.link_id}
                              onClick={() => void resend(link)}
                              aria-label={`Resend link to ${link.recipient_email}`}
                            >
                              Resend
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              disabled={busyLinkId === link.link_id}
                              onClick={() => void revoke(link)}
                              aria-label={`Revoke link for ${link.recipient_email}`}
                            >
                              Revoke
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
